/**
 * ############################################################################
 * # MIRROR FILE — change this and `functions/src/utils/versionedFilename.ts` #
 * # TOGETHER, in the same commit. They are hand-maintained copies.           #
 * ############################################################################
 *
 * Frontend mirror of `functions/src/utils/versionedFilename.ts`.
 *
 * Used by the Issue Version modal to preview the canonical filename
 * before the user confirms. The Cloud Function uses the backend copy
 * to actually write the file to Storage; both must produce identical
 * output for the same input. If they diverge, the browser tells the user
 * one filename and the server writes a different one — a discrepancy that
 * surfaces only after the file is issued.
 *
 * They are NOT deduplicated on purpose: `web/` has no test runner and most of
 * its node_modules is git-tracked, so it must not gain a dependency on the
 * functions package. The mirror is exercised by
 * `web/src/utils/versionedFilename.test.ts`, which is run from the functions
 * package via `npm run test:web` and keeps identical cases to the
 * functions-side suite.
 */

export interface VersionedFilenameInput {
  shortName: string | null | undefined;
  protocolNumber?: string | null;
  docCode: string;
  major: number;
  minor: number;
  issueDate: Date;
  extension?: string;
}

export function buildVersionedFilename(input: VersionedFilenameInput): string {
  const {
    shortName,
    protocolNumber,
    docCode,
    major,
    minor,
    issueDate,
    extension = "docx",
  } = input;

  const projectToken = resolveProjectToken(shortName, protocolNumber);
  const dateToken = formatYyyymmdd(issueDate);

  return `${projectToken}_${docCode}_v${major}.${minor}_${dateToken}.${extension}`;
}

function resolveProjectToken(
  shortName: string | null | undefined,
  protocolNumber: string | null | undefined,
): string {
  const sanitizedShort = sanitize(shortName);
  if (sanitizedShort) return sanitizedShort;

  const sanitizedProtocol = sanitize(protocolNumber).slice(0, 8);
  if (sanitizedProtocol) return sanitizedProtocol;

  return "PROJECT";
}

function sanitize(input: string | null | undefined): string {
  if (!input) return "";
  return input.replace(/[^a-zA-Z0-9]/g, "");
}

/**
 * The study token EXACTLY as a filename will carry it.
 *
 * Exported so the uniqueness check can compare on this spelling rather than on
 * the stored one. The two differ: `sanitizeStudyToken` keeps hyphens and
 * underscores, this drops them. Comparing uniqueness on the stored spelling let
 * `ONC-01` and `ONC01` both exist while producing one filename, which is the
 * collision the check exists to prevent.
 */
export function filenameStudyToken(input: string | null | undefined): string {
  return sanitize(input);
}

/** Longest study token a filename will carry. */
export const STUDY_TOKEN_MAX_LENGTH = 40;

/**
 * Sanitize the study identifier that goes into a deliverable filename.
 *
 * The study Short Name is user-supplied free text, and it is about to be
 * stamped on every file a sponsor receives — so it has to survive a
 * filesystem, a zip entry, a `Content-Disposition` header and an email
 * attachment unchanged. The rules:
 *
 *   1. Anything that is not alphanumeric, hyphen or underscore is removed.
 *      Whitespace runs collapse to a single underscore FIRST, so "ONC 2024"
 *      reads as "ONC_2024" rather than "ONC2024" — a space is a word break
 *      and deserves to survive as one. Every other punctuation mark
 *      (slashes above all) is simply dropped; turning a slash into a
 *      separator would invent a word break that the user did not type, and
 *      leaving it in would invent a directory.
 *   2. Leading and trailing separators are trimmed, so the token never
 *      collides with the `_` that joins filename segments.
 *   3. The result is capped, then re-trimmed — capping can expose an
 *      interior separator at the boundary, and a trailing "_" would read as
 *      a missing segment rather than a truncation.
 *
 * Non-ASCII is dropped rather than transliterated: a transliteration table
 * is a guess about a language we were not told, and a filename that differs
 * by locale is worse than one that is plainly shortened.
 *
 * Returns "" when nothing survives. Callers MUST treat "" as "no usable
 * short name" and fall back — never substitute a literal default here, which
 * would collide across studies.
 */
export function sanitizeStudyToken(input: string | null | undefined): string {
  if (typeof input !== "string") return "";
  const collapsed = input.replace(/\s+/g, "_");
  const kept = collapsed.replace(/[^A-Za-z0-9_-]/g, "");
  const trimmed = kept.replace(/^[-_]+/, "").replace(/[-_]+$/, "");
  if (trimmed.length <= STUDY_TOKEN_MAX_LENGTH) return trimmed;
  return trimmed.slice(0, STUDY_TOKEN_MAX_LENGTH).replace(/[-_]+$/, "");
}

function formatYyyymmdd(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

/**
 * Document-family codes that appear in versioned filenames. Mirrors
 * `DOC_CODES` in `functions/src/utils/versionedFilename.ts`.
 */
export const DOC_CODES = {
  dmp: "DMP",
  urs: "URS",
  frs: "FRS",
  sds: "SDS",
  ads: "ADS",
  atds: "ATDS",
  sacs: "SACS",
  iis: "IIS",
  testPlan: "TestPlan",
  testProcedure: "TestProcedure",
  testList: "TestList",
  testReport: "TestReport",
  testIncidentReport: "TestIncidentReport",
  uat: "UAT",
  uatReport: "UATReport",
  edts: "EDTS",
  drcp: "DRCP",
  saerp: "SAERP",
  dblc: "DBLC",
  mcs: "MCS",
  crf: "CRF",
  icf: "ICF",
  editChecks: "EditChecks",
  dataDictionary: "DataDictionary",
  sysReq: "SysReq",
  sysReqAttrs: "SysReqAttrs",
  dbPhysModel: "DBPhysModel",
  dbAttrs: "DBAttrs",
  ifaceFeatures: "InterfaceFeatures",
  ifaceAttrs: "InterfaceAttrs",
  acrf: "aCRF",
  odm: "ODM",
} as const;

export type DocCodeKey = keyof typeof DOC_CODES;
